import React, { useEffect, useRef, useState } from 'react';
import Modal from 'components/shared/Modal';
import ModalFooter from 'components/shared/modal/ModalFooter';
import { ModalRouteProps } from 'context/ModalRouter';
import { useMutation, useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import UserApi from 'api/user';
import Logger from 'utils/logger';
import { ApiClientError } from 'api/client';
import { queryKey } from 'constants/queryKey';
import { Alert, Button, Icon } from '@kontentino/ui';
import { faUserPlus } from '@fortawesome/pro-regular-svg-icons/faUserPlus';
import InviteUserForm, { InviteUserFormState } from './InviteUserForm';
import InviteUserSuccessModal from './InviteUserSuccessModal';
import { InviteUserResponse } from 'types/User';
import { useTranslation } from 'react-i18next';
import { useSeatLimitErrorHandler } from 'app/utils/useSeatLimitErrorHandler';
import useSubscriptionInfo from 'app/hooks/useSubscriptionInfo';
import { modalRoutes } from 'constants/routes';
import { AccountThunks } from 'modules/account/accountActions';

const InviteUserModal = (props: ModalRouteProps) => {
  const { modalManager, modalHistory } = props;
  const [isFormValid, setIsFormValid] = useState(false);
  const [inviteResponse, setInviteResponse] =
    useState<InviteUserResponse | null>(null);
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const handleSeatLimitError = useSeatLimitErrorHandler();
  const { hasReachedUsersLimit, userSeats } = useSubscriptionInfo();
  const isAtSeatLimit = hasReachedUsersLimit();
  const hasRedirectedToLimitModalRef = useRef(false);
  const [hasConfirmedBelowLimit, setHasConfirmedBelowLimit] = useState(false);

  useEffect(() => {
    if (hasRedirectedToLimitModalRef.current) return;
    if (hasConfirmedBelowLimit) return;
    if (!userSeats) return;

    if (isAtSeatLimit) {
      hasRedirectedToLimitModalRef.current = true;
      modalHistory.replaceLast({
        hash: modalRoutes.USER_LIMIT_REACHED,
      });
      modalManager.destroy();
    } else {
      setHasConfirmedBelowLimit(true);
    }
  }, [
    isAtSeatLimit,
    userSeats,
    modalHistory,
    modalManager,
    hasConfirmedBelowLimit,
  ]);

  const sendInvitationMutation = useMutation(
    (values: InviteUserFormState) => UserApi.sendInvitation(values),
    {
      onSuccess: (data: InviteUserResponse) => {
        setInviteResponse(data);
        dispatch(AccountThunks.requestAccount());
        queryClient.invalidateQueries(queryKey.usersList());
      },
      onError: (error: ApiClientError) => {
        if (handleSeatLimitError(error)) {
          sendInvitationMutation.reset();
          return;
        }
        Logger.error(error);
      },
    },
  );

  function onSubmit(values: InviteUserFormState) {
    sendInvitationMutation.mutate(values);
  }

  const handleSuccessModalClose = () => {
    setInviteResponse(null);
    modalManager.close();
  };

  if (isAtSeatLimit && !hasConfirmedBelowLimit) {
    return null;
  }

  if (inviteResponse) {
    return (
      <InviteUserSuccessModal
        isOpen={true}
        onClose={handleSuccessModalClose}
        inviteResponse={inviteResponse}
      />
    );
  }

  return (
    <Modal
      modalManager={modalManager}
      size="sm"
      dataName={{
        wrapper: 'invite-user-modal',
      }}
    >
      <Modal.Header
        title={t('inviteUserModal.inviteTeamMemberTitle')}
        className="tw-pb-0"
      />
      <Modal.Content className="tw-py-4">
        {sendInvitationMutation.isError && (
          <Alert variant="danger" className="tw-mb-4">
            {sendInvitationMutation.error?.userMessage ??
              t('inviteUserModal.somethingWentWrong')}
          </Alert>
        )}

        <InviteUserForm
          onSubmit={onSubmit}
          isSubmitting={sendInvitationMutation.isLoading}
          onValidityChange={setIsFormValid}
          serverError={sendInvitationMutation.error?.userMessage || ''}
        />
      </Modal.Content>
      <ModalFooter withDivider className="tw-flex tw-justify-center">
        <Button
          type="submit"
          form="invite-user-form"
          disabled={sendInvitationMutation.isLoading || !isFormValid}
          iconBefore={<Icon icon={faUserPlus} />}
          isLoading={sendInvitationMutation.isLoading}
          className="tw-w-full"
        >
          {t('inviteUserModal.inviteTeamMemberButton')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default InviteUserModal;
